/**
 * Line Detection Service
 *
 * Provides line detection functionality using computer vision techniques.
 * Analyzes captured images to detect straight lines in the scene.
 * Uses a configurable pipeline for processing.
 */

import * as THREE from 'three';
import { DetectedLine, LineDetectionResult, LineDetectionConfig } from './types';
import { Viewer } from '../../../../viewer/viewer';
import { CoordinateTransformer } from '../utils/CoordinateTransformer';
import { LineDetectionPipeline } from './LineDetectionPipeline';
import { LineDetectionWorkerManager, DepthAutoPrompt, RegionGraph, ProcessedRegionPolygon, PreviewMaskResult, PreviewRectResult, UpdateSelectionResult } from './LineDetectionWorkerManager';
import type { PixelAlignmentDebugData } from '../../../../utils/PolygonProcessor';
import { ImageCapture } from '../capture/ImageCapture';

export interface LineDetectionDebugData {
  capturedImage: ImageData;
  grayscaleImage: ImageData;
  edgesImage: ImageData;
  rawLines: Array<{ x1: number; y1: number; x2: number; y2: number; votes: number }>;
  filteredLines: Array<{ x1: number; y1: number; x2: number; y2: number; votes: number }>;
  detectionTime: number;
}

export class LineDetectionService {
  private viewer: Viewer;
  private coordinateTransformer: CoordinateTransformer;
  private imageCapture: ImageCapture | null = null;
  private pipeline: LineDetectionPipeline;
  private workerManager: LineDetectionWorkerManager;
  private config: Required<LineDetectionConfig>;
  private nextLineId = 0;
  public lastDebugData: LineDetectionDebugData | null = null;
  /** Position map from the last capture — kept until camera moves (cleared in cancel()) */
  public cachedPositionMap: ImageData | null = null;
  /** Eye-space frustum bounds used for the cached position map's encoding. */
  public cachedEyeMin: [number, number, number] | null = null;
  public cachedEyeMax: [number, number, number] | null = null;
  /** Inverse view matrix of the camera at capture time. Multiply decoded eye XYZ by this to recover world. */
  public cachedInverseViewMatrix: THREE.Matrix4 | null = null;
  /** Density map from the last capture — the viewport image used for pixel alignment fallback */
  public cachedDensityMap: ImageData | null = null;
  /** Auto-prompts returned from the last depth segmentation */
  public lastAutoPrompts: DepthAutoPrompt[] | null = null;
  /** Region graph from the last depth segmentation */
  public lastRegionGraph: RegionGraph | null = null;
  /** Whether to show the region graph (debug option from pipeline) */
  public lastShowRegionGraph = false;
  /** Colored segmentation data from the last depth segmentation */
  public lastSegmentationColoredData: Uint8ClampedArray | null = null;
  public lastSegmentationWidth = 0;
  public lastSegmentationHeight = 0;
  /** Pixel alignment debug data from the last polygon processing */
  public lastPixelAlignmentDebug: PixelAlignmentDebugData | null = null;

  /**
   * Supplies world-space polygons of existing annotations (e.g. committed
   * rooms) that depth segmentation must punch out via the
   * remove_invalid_areas pass. Set by the owning line mode; called at
   * segmentation time so the list is always current.
   */
  public excludedWorldPolygonsProvider: (() => THREE.Vector3[][]) | null = null;

  /** Camera corners at the time of the last segmentation - used to validate segmentation results are current */
  public segmentationCameraCorners: THREE.Vector3[] | null = null;
  /** Camera corners at the time of the last line detection - used to validate line detection results are current */
  public lineDetectionCameraCorners: THREE.Vector3[] | null = null;

  constructor(viewer: Viewer, coordinateTransformer: CoordinateTransformer, config: LineDetectionConfig = {}, imageCapture?: ImageCapture, polygonPipelineConfigPath?: string, depthPipelineConfigPath?: string) {
    this.viewer = viewer;
    this.coordinateTransformer = coordinateTransformer;
    this.imageCapture = imageCapture ?? null;
    this.config = {
      minConfidence: config.minConfidence ?? 0.7,
      minLineLength: config.minLineLength ?? 100,
      maxLineGap: config.maxLineGap ?? 10,
      cannyLowThreshold: config.cannyLowThreshold ?? 80,
      cannyHighThreshold: config.cannyHighThreshold ?? 150,
      houghThreshold: config.houghThreshold ?? 150,
    };

    // Initialize pipeline (for config UI)
    this.pipeline = this.buildPipeline();

    // Worker manager owns the polygon + depth pipelines; route through the kind-specific config paths.
    this.workerManager = new LineDetectionWorkerManager(polygonPipelineConfigPath, depthPipelineConfigPath);
  }

  /**
   * Capture both position and density maps for the current viewport.
   * Updates CoordinateTransformer with mask corners and captured image dimensions.
   * Call this before detection/segmentation methods.
   *
   * @param zRange Optional z-range from floor isolation plane for position map depth filtering
   * @param worldBoxCorners Optional 8-corner oriented bounding box used to derive a tight eye-Z
   * range. Supersedes `zRange` when supplied — needed for non-axis-aligned captures (the
   * door/window wall view, where the camera looks along a horizontal axis instead of -Z and a
   * world-Z slab would over-estimate the eye-Z range).
   */
  async captureViewportMaps(zRange?: [number, number], worldBoxCorners?: THREE.Vector3[]): Promise<{
    positionMap: ImageData;
    densityMap: ImageData;
    worldCorners: THREE.Vector3[];
  } | null> {
    if (!this.imageCapture) return null;

    const viewportWidth = this.viewer.renderArea.clientWidth;
    const viewportHeight = this.viewer.renderArea.clientHeight;
    const viewStart = new THREE.Vector2(0, 0);
    const viewEnd = new THREE.Vector2(viewportWidth, viewportHeight);

    // Capture position map
    const posMapResult = this.imageCapture.capturePositionMap(viewStart, viewEnd, viewportWidth, viewportHeight, zRange, worldBoxCorners);
    const positionMap = posMapResult?.imageData ?? null;
    if (!positionMap || !posMapResult) return null;

    // Cache the eye-space encoding metadata so worker / decoder code can
    // reconstruct world position from each encoded pixel via
    // `world = inverseViewMatrix * eyeXYZ`.
    this.cachedEyeMin = posMapResult.eyeMin;
    this.cachedEyeMax = posMapResult.eyeMax;
    this.cachedInverseViewMatrix = posMapResult.inverseViewMatrix;

    // Compute world corners from screen corners at this exact moment
    const screenCorners = [new THREE.Vector2(0, 0), new THREE.Vector2(viewportWidth, 0), new THREE.Vector2(viewportWidth, viewportHeight), new THREE.Vector2(0, viewportHeight)];
    const worldCorners: THREE.Vector3[] = [];
    for (const corner of screenCorners) {
      const worldPos = this.coordinateTransformer.screenToWorld(corner);
      if (!worldPos) return null;
      worldCorners.push(worldPos);
    }

    // Update CoordinateTransformer with mask corners and image dimensions
    this.coordinateTransformer.setMaskCorners(worldCorners);
    const canvas = document.createElement('canvas');
    canvas.width = positionMap.width;
    canvas.height = positionMap.height;
    this.coordinateTransformer.setCapturedImage({
      canvas,
      width: positionMap.width,
      height: positionMap.height,
    });

    // Store the eye-space encoding so any reader of cachedPositionMap (this
    // process AND worker-side via WorkerManager) can decode pixels back to
    // world position via inverse view matrix.
    this.coordinateTransformer.setPositionMapEncoding({
      eyeMin: posMapResult.eyeMin,
      eyeMax: posMapResult.eyeMax,
      inverseViewMatrix: posMapResult.inverseViewMatrix,
    });

    // Cache position map
    this.cachedPositionMap = positionMap;

    // Capture density map (viewport color image)
    const densityMap = await this.captureViewport();
    this.cachedDensityMap = densityMap;

    return { positionMap, densityMap, worldCorners: worldCorners.map((c) => c.clone()) };
  }

  /**
   * Build the line detection pipeline with default passes
   */
  private buildPipeline(): LineDetectionPipeline {
    const pipeline = new LineDetectionPipeline();

    // Try to load config from file first
    const configPath = '/config/segmentation/line-detection-pipeline-config.json';
    pipeline.loadFromFile(configPath).catch(() => {
      // If config file doesn't exist, build default pipeline
      console.log('[LineDetectionService] Config file not found, using default pipeline configuration');

      // Add default passes using registry
      const grayscalePass = pipeline.processorRegistry.get('grayscale_conversion');
      if (grayscalePass) {
        pipeline.addPass('Grayscale Conversion', grayscalePass.func, {
          processorId: 'grayscale_conversion',
          kwargs: {},
        });
      }

      const cannyPass = pipeline.processorRegistry.get('canny_edge_detection');
      if (cannyPass) {
        pipeline.addPass('Canny Edge Detection', cannyPass.func, {
          processorId: 'canny_edge_detection',
          kwargs: {
            cannyLowThreshold: this.config.cannyLowThreshold,
            cannyHighThreshold: this.config.cannyHighThreshold,
          },
        });
      }

      const houghPass = pipeline.processorRegistry.get('hough_line_transform');
      if (houghPass) {
        pipeline.addPass('Hough Line Transform', houghPass.func, {
          processorId: 'hough_line_transform',
          kwargs: {
            houghThreshold: this.config.houghThreshold,
          },
        });
      }

      const filterPass = pipeline.processorRegistry.get('filter_lines');
      if (filterPass) {
        pipeline.addPass('Filter Lines', filterPass.func, {
          processorId: 'filter_lines',
          kwargs: {
            minConfidence: this.config.minConfidence,
            minLineLength: this.config.minLineLength,
          },
        });
      }

      const mergePass = pipeline.processorRegistry.get('merge_cartesian_lines');
      if (mergePass) {
        pipeline.addPass('Merge Similar Lines', mergePass.func, {
          processorId: 'merge_cartesian_lines',
          kwargs: {},
        });
      }

      const qualityPass = pipeline.processorRegistry.get('verify_line_quality');
      if (qualityPass) {
        pipeline.addPass('Verify Line Quality', qualityPass.func, {
          processorId: 'verify_line_quality',
          kwargs: {
            qualityThreshold: 0.3,
          },
        });
      }

      const refinePass = pipeline.processorRegistry.get('refine_endpoints');
      if (refinePass) {
        pipeline.addPass('Refine Endpoints', refinePass.func, {
          processorId: 'refine_endpoints',
          kwargs: {
            refinementSearchRange: 20,
            refinementIntensityThreshold: 60,
            refinementSignificanceThreshold: 0.3,
          },
        });
      }
    });

    return pipeline;
  }

  /**
   * Get the line detection pipeline instance for UI integration
   */
  getPipeline(): LineDetectionPipeline {
    return this.pipeline;
  }

  /**
   * Get the depth segmentation pipeline instance for UI integration.
   * Changes made to this pipeline must be synced to the worker via updateDepthPipelineConfig().
   */
  getDepthPipeline() {
    return this.workerManager.depthPipeline;
  }

  /**
   * Sync the current depth pipeline config to the worker after UI changes.
   */
  async updateDepthPipelineConfig(): Promise<void> {
    return this.workerManager.updateDepthPipeline();
  }

  /**
   * Get the polygon processing pipeline instance for UI integration.
   * Changes made to this pipeline must be synced to the worker via updatePolygonPipelineConfig().
   */
  getPolygonPipeline() {
    return this.workerManager.polygonPipeline;
  }

  /**
   * Sync the current polygon pipeline config to the worker after UI changes.
   */
  async updatePolygonPipelineConfig(): Promise<void> {
    return this.workerManager.updatePolygonPipeline();
  }

  /**
   * Capture the current viewport and detect lines.
   * Maps should be captured via captureViewportMaps() before calling this method.
   * Falls back to capturing density map internally if not already cached.
   * @param signal Optional AbortSignal to cancel the detection
   */
  async detectLines(signal?: AbortSignal): Promise<LineDetectionResult> {
    const startTime = performance.now();

    // Check for abort before starting
    if (signal?.aborted) {
      throw new DOMException('Detection aborted', 'AbortError');
    }

    // Use cached density map if available, otherwise capture now
    const imageData = this.cachedDensityMap ?? (await this.captureViewport());
    this.cachedDensityMap = imageData;

    // Check for abort after capture
    if (signal?.aborted) {
      throw new DOMException('Detection aborted', 'AbortError');
    }

    const lines = await this.processImage(imageData, signal);

    const detectionTime = performance.now() - startTime;

    // Store detection time in debug data if available
    if (this.lastDebugData) {
      this.lastDebugData.detectionTime = detectionTime;
    }

    return {
      lines,
      imageData,
      timestamp: Date.now(),
    };
  }

  /**
   * Run only depth segmentation (no line detection).
   * Maps should be captured via captureViewportMaps() before calling this method.
   */
  async runSegmentationOnly(): Promise<void> {
    const positionMap = this.cachedPositionMap;
    if (!positionMap) return;

    // Use cached density map or capture now
    if (!this.cachedDensityMap) {
      this.cachedDensityMap = await this.captureViewport();
    }

    // Get world corners from coordinate transformer
    const worldCorners = this.coordinateTransformer.getMaskCorners();
    if (worldCorners.length !== 4) return;

    // Convert corners to plain objects for worker transfer
    const cornersForWorker = worldCorners.map((c) => ({ x: c.x, y: c.y, z: c.z }));

    // Pass corners to worker - they will be stored alongside the depth state
    // and returned with every response, ensuring they stay in sync
    const result = await this.workerManager.processSegmentationOnly(positionMap, cornersForWorker, this.cachedDensityMap, this.getExcludedPolygonsInMaskSpace());

    // Store corners for result validation check (areSegmentationResultsValid)
    this.segmentationCameraCorners = worldCorners.map((c) => c.clone());

    // Store results - corners are now managed by the worker and returned with each response
    this.lastAutoPrompts = result.autoPrompts ?? null;
    this.lastRegionGraph = result.regionGraph ?? null;
    this.lastShowRegionGraph = result.showRegionGraph ?? false;
    this.lastSegmentationColoredData = result.segmentationColoredData ?? null;
    this.lastSegmentationWidth = result.segmentationWidth ?? 0;
    this.lastSegmentationHeight = result.segmentationHeight ?? 0;
  }

  /**
   * Convert the provider's world-space annotation polygons to normalized
   * mask space [0,1] for the remove_invalid_areas pass. Requires mask
   * corners from the latest capture; returns undefined when unavailable.
   */
  private getExcludedPolygonsInMaskSpace(): Array<Array<{ x: number; y: number }>> | undefined {
    if (!this.excludedWorldPolygonsProvider) return undefined;
    if (this.coordinateTransformer.getMaskCorners().length !== 4) return undefined;

    const polygons: Array<Array<{ x: number; y: number }>> = [];
    for (const worldPolygon of this.excludedWorldPolygonsProvider()) {
      if (worldPolygon.length < 3) continue;
      polygons.push(
        worldPolygon.map((point) => {
          const maskPos = this.coordinateTransformer.worldToMaskSpace(point);
          return { x: maskPos.x, y: maskPos.y };
        }),
      );
    }
    return polygons.length > 0 ? polygons : undefined;
  }

  /**
   * Check if two sets of camera corners match (within small tolerance)
   */
  private cornersMatch(corners1: THREE.Vector3[], corners2: THREE.Vector3[]): boolean {
    if (corners1.length !== 4 || corners2.length !== 4) {
      return false;
    }
    const TOLERANCE = 0.001; // Small tolerance for floating point comparison
    for (let i = 0; i < 4; i++) {
      if (corners1[i].distanceTo(corners2[i]) > TOLERANCE) {
        return false;
      }
    }
    return true;
  }

  /**
   * Check if segmentation results are still valid for the current camera view
   */
  areSegmentationResultsValid(): boolean {
    if (!this.segmentationCameraCorners || this.segmentationCameraCorners.length !== 4) {
      return false;
    }
    const currentCorners = this.coordinateTransformer.getMaskCorners();
    return this.cornersMatch(this.segmentationCameraCorners, currentCorners);
  }

  /**
   * Check if line detection results are still valid for the current camera view
   */
  areLineDetectionResultsValid(): boolean {
    if (!this.lineDetectionCameraCorners || this.lineDetectionCameraCorners.length !== 4) {
      return false;
    }
    const currentCorners = this.coordinateTransformer.getMaskCorners();
    return this.cornersMatch(this.lineDetectionCameraCorners, currentCorners);
  }

  /**
   * Check if stored results are still valid for the current camera view
   * @deprecated Use areSegmentationResultsValid() or areLineDetectionResultsValid() instead
   */
  areResultsValidForCurrentView(): boolean {
    // For backwards compatibility, check both - return true if either is valid
    return this.areSegmentationResultsValid() || this.areLineDetectionResultsValid();
  }

  /**
   * Run only line detection (no depth segmentation).
   * Use when line prompts exist or Ctrl is pressed.
   * @param signal Optional AbortSignal to cancel the detection
   * @param positionMap Optional position map for pixel-aligned line detection
   * @param worldCorners Optional world-space corners captured at the same time as the position map
   */
  async detectLinesOnly(signal?: AbortSignal): Promise<LineDetectionResult> {
    const startTime = performance.now();

    // NOTE: Do NOT store lineDetectionCameraCorners here - wait until after processing completes
    // Otherwise there's a race condition where preview requests during processing get wrong corners

    // Check for abort before starting
    if (signal?.aborted) {
      throw new DOMException('Detection aborted', 'AbortError');
    }

    // Use cached density map if available, otherwise capture now
    const imageData = this.cachedDensityMap ?? (await this.captureViewport());
    this.cachedDensityMap = imageData;

    // Get world corners from coordinate transformer
    const requestCorners = this.coordinateTransformer.getMaskCorners().map((c) => c.clone());

    // Check for abort after capture
    if (signal?.aborted) {
      throw new DOMException('Detection aborted', 'AbortError');
    }

    // Wait for pipeline config to load from file before processing
    await this.pipeline.waitForLoad();

    // Calculate cm to pixels ratio for the worker
    const pixelsPerMeter = this.coordinateTransformer.calculatePixelsPerMeter();
    const cmToPixelsRatio = pixelsPerMeter !== undefined ? pixelsPerMeter / 100 : null;

    // Run line detection only (no segmentation)
    const result = await this.workerManager.processLinesOnly(imageData, JSON.stringify(this.pipeline.getConfig()), cmToPixelsRatio, this.cachedPositionMap);

    // Copy worker's pass timings to main thread pipeline for UI display
    this.pipeline.passTimings.clear();
    result.passTimings.forEach((value, key) => {
      this.pipeline.passTimings.set(key, value);
    });

    // Store debug data
    this.lastDebugData = {
      capturedImage: result.debugData.capturedImage,
      grayscaleImage: result.debugData.grayscaleImage,
      edgesImage: result.debugData.edgesImage,
      rawLines: result.debugData.rawLines,
      filteredLines: result.debugData.filteredLines,
      detectionTime: performance.now() - startTime,
    };

    // Store corners after processing completes
    this.lineDetectionCameraCorners = requestCorners;

    // Convert 2D lines to 3D DetectedLines
    const lines = this.convertToDetectedLines(result.lines, imageData.width, imageData.height);

    return {
      lines,
      imageData,
      timestamp: Date.now(),
    };
  }

  /**
   * Capture the current viewport as ImageData
   * Using screenshotRenderer approach for proper encoding but at viewport resolution
   */
  private async captureViewport(): Promise<ImageData> {
    const width = this.viewer.renderArea.clientWidth;
    const height = this.viewer.renderArea.clientHeight;

    // Calculate WebGL origin (bottom-left coordinate system)
    const start = new THREE.Vector2(0, 0);
    const end = new THREE.Vector2(width, height);

    const webglOrigin = new THREE.Vector2(Math.min(start.x, end.x), Math.min(this.viewer.renderArea.clientHeight - start.y, this.viewer.renderArea.clientHeight - end.y));

    // Use screenshotRenderer with viewport dimensions for proper encoding
    // skipCrop=true ensures 1:1 pixel mapping between viewport and captured image
    const screenshotData = this.viewer.screenshotRenderer.renderScreenshot(
      webglOrigin,
      width,
      height,
      false, // renderObjects - don't render measurement objects etc.
      1.0, // resolution multiplier - use 1:1 for viewport capture
      true, // skipCrop - needed for accurate screen-to-world coordinate mapping
    );

    // Verify dimensions and buffer size
    const actualPixelWidth = Math.round(screenshotData.pixelWidth);
    const actualPixelHeight = Math.round(screenshotData.pixelHeight);
    const expectedBufferSize = actualPixelWidth * actualPixelHeight * 4;

    if (screenshotData.buffer.length !== expectedBufferSize) {
      console.error(`Buffer size mismatch: ${actualPixelWidth}x${actualPixelHeight} expects ${expectedBufferSize} bytes, got ${screenshotData.buffer.length}`);
      return new ImageData(1, 1);
    }

    // Convert buffer to ImageData
    const imageData = new ImageData(actualPixelWidth, actualPixelHeight);

    // Safety check before setting buffer
    if (imageData.data.length !== screenshotData.buffer.length) {
      console.error(`ImageData size mismatch: expects ${imageData.data.length} bytes, buffer has ${screenshotData.buffer.length} bytes`);
      return new ImageData(1, 1);
    }

    imageData.data.set(screenshotData.buffer);

    // Force all alpha values to 255 (fully opaque)
    for (let i = 3; i < imageData.data.length; i += 4) {
      imageData.data[i] = 255;
    }

    // Set up coordinate transformer for pixel-to-cm conversion
    // Only set mask corners if they haven't been configured already (e.g., by AutoRoomDetectTool)
    const existingMaskCorners = this.coordinateTransformer.getMaskCorners();
    if (existingMaskCorners.length === 0) {
      // Calculate mask corners from viewport screen corners
      const screenCorners = [
        new THREE.Vector2(0, 0), // top-left
        new THREE.Vector2(width, 0), // top-right
        new THREE.Vector2(width, height), // bottom-right
        new THREE.Vector2(0, height), // bottom-left
      ];

      const maskCorners = screenCorners.map((corner) => {
        const worldPos = this.coordinateTransformer.screenToWorld(corner);
        return worldPos || new THREE.Vector3();
      });

      this.coordinateTransformer.setMaskCorners(maskCorners);

      // Create a minimal canvas object for the CapturedImage interface
      // (only width/height are used by calculatePixelsPerMeter)
      const canvas = document.createElement('canvas');
      canvas.width = actualPixelWidth;
      canvas.height = actualPixelHeight;
      this.coordinateTransformer.setCapturedImage({
        canvas,
        width: actualPixelWidth,
        height: actualPixelHeight,
      });
    }

    // Update pipeline's cm to pixels converter now that coordinate transformer is ready
    this.pipeline.setCmToPixels((cm: number) => {
      const pixelsPerMeter = this.coordinateTransformer.calculatePixelsPerMeter();
      if (pixelsPerMeter === undefined) {
        return cm; // Return cm value as-is if conversion not available
      }
      return cm * (pixelsPerMeter / 100); // Convert cm to pixels (pixelsPerMeter / 100 = pixelsPerCm)
    });

    return imageData;
  }

  /**
   * Cancel any ongoing detection and clear the cached maps.
   * Call this on camera movement so stale depth state is not used for preview.
   */
  cancel(): void {
    this.workerManager.cancel();
    this.cachedPositionMap = null;
    this.cachedDensityMap = null;
    this.lastAutoPrompts = null;
    this.lastRegionGraph = null;
    this.lastShowRegionGraph = false;
    this.lastPixelAlignmentDebug = null;
    // Clear result corners so stale results won't be used
    this.segmentationCameraCorners = null;
    this.lineDetectionCameraCorners = null;
    // Note: We no longer clear coordinateTransformer here because:
    // 1. It breaks updatePreview if camera drifts after segmentation starts
    // 2. The transformer corners are re-set in capturePositionMap() before each new segmentation
    // 3. Masks/polygons now render in world space with their own captured corners
  }

  /**
   * Get a hover preview mask for the depth-segmentation region under the cursor.
   * Uses cached selection state from updateSelectedRegions().
   * @param normalizedX X in [0, 1] mask space
   * @param normalizedY Y in [0, 1] mask space
   * @returns Preview result with ImageData mask and resolved region ID, or null
   */
  async getPreviewMask(normalizedX: number, normalizedY: number): Promise<PreviewMaskResult | null> {
    return this.workerManager.preview(normalizedX, normalizedY);
  }

  /**
   * Get a rect preview: all regions within a rectangular brush area of the cursor.
   * Returns combined mask + list of region IDs found.
   * World corners are returned by the worker alongside the result.
   */
  async getPreviewRect(normalizedX: number, normalizedY: number, halfSizePixels: number): Promise<PreviewRectResult | null> {
    // Worker returns worldCorners alongside the result - no need to attach separately
    return this.workerManager.previewRect(normalizedX, normalizedY, halfSizePixels);
  }

  /**
   * Update the cached selected regions in the worker.
   * Call this when the selection changes to pre-compute the selection mask.
   * This avoids recomputing the selection mask on every hover.
   * World corners are returned by the worker alongside the result.
   */
  async updateSelectedRegions(selectedRegionIds: number[]): Promise<UpdateSelectionResult> {
    // Worker returns worldCorners alongside the result - no need to attach separately
    return this.workerManager.updateSelectedRegions(selectedRegionIds);
  }

  /**
   * Paint a rectangle directly into the worker's selection mask.
   * Returns the updated mask + any new region IDs found in the rectangle.
   * World corners are returned by the worker alongside the result.
   */
  async paintRect(normalizedX: number, normalizedY: number, halfSizePixels: number): Promise<PreviewRectResult | null> {
    // Worker returns worldCorners alongside the result - no need to attach separately
    return this.workerManager.paintRect(normalizedX, normalizedY, halfSizePixels);
  }

  /**
   * Paint multiple brush rectangles in one batch (fast path — no expandRegions).
   * Returns the accumulated display mask + direct region hits.
   */
  async paintRectBatch(positions: Array<{ normalizedX: number; normalizedY: number; halfSizePixels: number }>): Promise<PreviewRectResult | null> {
    return this.workerManager.paintRectBatch(positions);
  }

  /**
   * Finalize the brush selection: run expandRegions + build clean mask.
   * Called once on mouse-up after batched painting.
   */
  async finalizeBrushSelection(): Promise<PreviewRectResult | null> {
    return this.workerManager.finalizeBrushSelection();
  }

  /**
   * Process image data to detect lines using the worker
   * The worker can be terminated immediately for instant cancellation
   */
  private async processImage(imageData: ImageData, _signal?: AbortSignal): Promise<DetectedLine[]> {
    const width = imageData.width;
    const height = imageData.height;

    // Wait for pipeline config to load from file before processing
    await this.pipeline.waitForLoad();

    // Calculate cm to pixels ratio for the worker
    const pixelsPerMeter = this.coordinateTransformer.calculatePixelsPerMeter();
    const cmToPixelsRatio = pixelsPerMeter !== undefined ? pixelsPerMeter / 100 : null;

    // Run processing in worker (can be cancelled by terminating worker)
    const result = await this.workerManager.process(imageData, JSON.stringify(this.pipeline.getConfig()), cmToPixelsRatio, this.cachedPositionMap, this.getExcludedPolygonsInMaskSpace());

    // Store auto-prompts and region graph from depth segmentation
    this.lastAutoPrompts = result.autoPrompts ?? null;
    this.lastRegionGraph = result.regionGraph ?? null;
    this.lastShowRegionGraph = result.showRegionGraph ?? false;
    this.lastSegmentationColoredData = result.segmentationColoredData ?? null;
    this.lastSegmentationWidth = result.segmentationWidth ?? 0;
    this.lastSegmentationHeight = result.segmentationHeight ?? 0;

    // Copy worker's pass timings to main thread pipeline for UI display
    this.pipeline.passTimings.clear();
    result.passTimings.forEach((value, key) => {
      this.pipeline.passTimings.set(key, value);
    });

    // Store debug data
    this.lastDebugData = {
      capturedImage: result.debugData.capturedImage,
      grayscaleImage: result.debugData.grayscaleImage,
      edgesImage: result.debugData.edgesImage,
      rawLines: result.debugData.rawLines,
      filteredLines: result.debugData.filteredLines,
      detectionTime: 0, // Will be set by detectLines()
    };

    // Convert 2D lines to 3D DetectedLines
    return this.convertToDetectedLines(result.lines, width, height);
  }

  /**
   * Convert detected 2D lines to DetectedLine objects with 3D coordinates
   */
  private convertToDetectedLines(lines: Array<{ x1: number; y1: number; x2: number; y2: number; votes: number }>, width: number, height: number): DetectedLine[] {
    const detectedLines: DetectedLine[] = [];

    for (const line of lines) {
      // Pixel coordinates from image match screen coordinates (1:1 resolution)
      const startScreen = new THREE.Vector2(line.x1, line.y1);
      const endScreen = new THREE.Vector2(line.x2, line.y2);

      // Use CoordinateTransformer to convert screen to world space
      const start3D = this.coordinateTransformer.screenToWorld(startScreen);
      const end3D = this.coordinateTransformer.screenToWorld(endScreen);

      if (start3D && end3D) {
        const direction = new THREE.Vector3().subVectors(end3D, start3D).normalize();
        const confidence = Math.min(1.0, line.votes / 100);

        detectedLines.push({
          id: `line_${this.nextLineId++}`,
          start: start3D.clone(),
          end: end3D.clone(),
          direction: direction,
          screenStart: startScreen.clone(),
          screenEnd: endScreen.clone(),
          confidence: confidence,
          isHovered: false,
          isSelected: false,
          isHidden: false,
        });
      }
    }

    return detectedLines;
  }

  /**
   * Update configuration
   */
  updateConfig(config: Partial<LineDetectionConfig>): void {
    Object.assign(this.config, config);

    // Update pipeline pass kwargs if they exist
    const cannyPass = this.pipeline.getPassByName('Canny Edge Detection');
    if (cannyPass && (config.cannyLowThreshold !== undefined || config.cannyHighThreshold !== undefined)) {
      this.pipeline.updatePassKwargs('Canny Edge Detection', {
        ...cannyPass.kwargs,
        cannyLowThreshold: config.cannyLowThreshold ?? this.config.cannyLowThreshold,
        cannyHighThreshold: config.cannyHighThreshold ?? this.config.cannyHighThreshold,
      });
    }

    const houghPass = this.pipeline.getPassByName('Hough Line Transform');
    if (houghPass && config.houghThreshold !== undefined) {
      this.pipeline.updatePassKwargs('Hough Line Transform', {
        ...houghPass.kwargs,
        houghThreshold: config.houghThreshold ?? this.config.houghThreshold,
      });
    }

    const filterPass = this.pipeline.getPassByName('Filter Lines');
    if (filterPass && (config.minConfidence !== undefined || config.minLineLength !== undefined)) {
      this.pipeline.updatePassKwargs('Filter Lines', {
        ...filterPass.kwargs,
        minConfidence: config.minConfidence ?? this.config.minConfidence,
        minLineLength: config.minLineLength ?? this.config.minLineLength,
      });
    }
  }

  /**
   * Process a depth-segmentation region into a polygon using the full PolygonProcessor pipeline.
   * @param normalizedX X in [0, 1] mask space
   * @param normalizedY Y in [0, 1] mask space
   * @returns Processed polygon in pixel space, or null if no region found
   */
  async processRegionPolygon(normalizedX: number, normalizedY: number): Promise<ProcessedRegionPolygon | null> {
    const pixelsPerMeter = this.coordinateTransformer.calculatePixelsPerMeter();
    if (pixelsPerMeter === undefined) {
      return null;
    }

    const result = await this.workerManager.processRegion(normalizedX, normalizedY, pixelsPerMeter, this.cachedPositionMap, this.cachedDensityMap);

    // Store debug data if present
    if (result?.pixelAlignmentDebug) {
      this.lastPixelAlignmentDebug = result.pixelAlignmentDebug;
    }

    // Attach the world corners that were used when capturing this segmentation
    if (result && this.segmentationCameraCorners) {
      result.worldCorners = this.segmentationCameraCorners.map((c) => ({ x: c.x, y: c.y, z: c.z }));
    }

    return result;
  }

  /**
   * Process a pre-built mask into a polygon.
   * Uses a mask already built by the sampling worker (e.g. from getPreviewMask()),
   * avoiding the line-detection worker needing to rebuild the mask from depth state.
   * This is the fast path for brush mode where the preview mask is already available.
   */
  async processRegionFromMask(maskData: Uint8ClampedArray, maskWidth: number, maskHeight: number, resolvedRegionId: number): Promise<ProcessedRegionPolygon | null> {
    const pixelsPerMeter = this.coordinateTransformer.calculatePixelsPerMeter();
    if (pixelsPerMeter === undefined) {
      return null;
    }

    const result = await this.workerManager.processRegionFromMask(maskData, maskWidth, maskHeight, resolvedRegionId, pixelsPerMeter, this.cachedPositionMap, this.cachedDensityMap);

    // Store debug data if present
    if (result?.pixelAlignmentDebug) {
      this.lastPixelAlignmentDebug = result.pixelAlignmentDebug;
    }

    // Attach the world corners that were used when capturing this segmentation
    if (result && this.segmentationCameraCorners) {
      result.worldCorners = this.segmentationCameraCorners.map((c) => ({ x: c.x, y: c.y, z: c.z }));
    }

    return result;
  }

  /**
   * Cleanup resources
   */
  dispose(): void {
    this.workerManager.dispose();
  }
}
